import {
  concat,
  GroupedObservable,
  MonoTypeOperatorFunction,
  Observable,
  ObservableInput,
  OperatorFunction,
  timer,
} from 'rxjs';
import { mergeMap, tap, throttle, groupBy, switchMap, take, debounceTime, exhaustMap, concatMap } from 'rxjs/operators';

/**
 *  This method, will ensure a stream of events in throttled so only first and last events make it through.
 *  The throttle works by waiting for an event's callback to complete before allowing any more through.
 *  This is particularly useful for the usecase where you want to synchrnize changes with the server, where you want to:
 *  A) On event immediately run callback (and ajax send an update)
 *  B) On further events, wait for previous callback to complete, throw away all but the latest event, then repeat (A).
 *
 *  The current implementation uses throttle() with local state to achieve the desired result, however it would be nice
 *  to improve this and write it without depending on other operators (for understanding purposes).
 *
 *  This was inspired by SO answers here:
 *  https://stackoverflow.com/questions/37146081/rxjs-throttle-behavior-get-first-value-immediately
 *
 *  pausePromise // This (promise) is used to wait for a previously running event to complete before starting a new one
 *  pausePromiseCompleteFn // This (func) is used to signal to the Promise above that it should complete.
 *  queueSize // This (counter) is used to test whether there are currently queued items waiting for completion.
 *
 * @param callback A stream that must return a value, not EMPTY. If nothing is returned this will never unblock.
 */
export const concatLatest: any = (callback: any) => (source: any) => {
  let pausePromise: Promise<unknown> | undefined = undefined;
  let pausePromiseCompleteFn: (_: any) => void = (_) => null;
  let queueSize = 0;
  return source.pipe(
    tap(() => ++queueSize),
    throttle(() => Promise.resolve(pausePromise), { leading: true, trailing: true }),
    tap(() => {
      pausePromise = new Promise((res) => (pausePromiseCompleteFn = res));
      queueSize = Math.max(0, queueSize - 1);
    }),
    mergeMap((action) => callback(action, () => queueSize === 0)),
    tap(() => {
      queueSize = 0;
      pausePromiseCompleteFn(undefined);
    })
  );
};

/**
 * Groups observables by a key and performs a switch map
 * such that only the final observable is emitted for each key
 * @param keySelector Returns key from input
 * @param mapFn Maps input to result
 * @param durationSelector [Optional] Selects an observable which will terminate the grouped observable when emitted (default emits after five minutes)
 */
export function switchMapByKey<TValue, TResult, TKey>(
  keySelector: (value: TValue) => TKey,
  mapFn: (value: TValue, index: number) => ObservableInput<TResult>,
  durationSelector: (group: GroupedObservable<TKey, TValue>) => Observable<any> = (_) => timer(300000)
): OperatorFunction<TValue, TResult> {
  return groupByKey(keySelector, (group) => group.pipe(switchMap(mapFn)), durationSelector);
}

/**
 * Groups observables by a key and performs an exhaustMap
 * such that we ignore new requests until the first one is exhausted
 * @param keySelector Returns key from input
 * @param mapFn Maps input to result
 * @param durationSelector [Optional] Selects an observable which will terminate the grouped observable when emitted (default emits after five minutes)
 */
export function exhaustMapByKey<TValue, TResult, TKey>(
  keySelector: (value: TValue) => TKey,
  mapFn: (value: TValue, index: number) => ObservableInput<TResult>,
  durationSelector: (group: GroupedObservable<TKey, TValue>) => Observable<any> = (_) => timer(300000)
): OperatorFunction<TValue, TResult> {
  return groupByKey(keySelector, (group) => group.pipe(exhaustMap(mapFn)), durationSelector);
}

export function concatMapByKey<TValue, TResult, TKey>(
  keySelector: (value: TValue) => TKey,
  mapFn: (value: TValue, index: number) => ObservableInput<TResult>,
  durationSelector: (group: GroupedObservable<TKey, TValue>) => Observable<any> = (_) => timer(300000)
): OperatorFunction<TValue, TResult> {
  return groupByKey(keySelector, (group) => group.pipe(concatMap(mapFn)), durationSelector);
}

/**
 * Groups observables by a key
 * @param keySelector Returns key from input
 * @param mapFn Maps group to result
 * @param durationSelector [Optional] Selects an observable which will terminate the grouped observable when emitted (default emits after five minutes)
 */
export function groupByKey<TValue, TResult, TKey>(
  keySelector: (value: TValue) => TKey,
  mapFn: (group: GroupedObservable<TKey, TValue>) => ObservableInput<TResult>,
  durationSelector: (group: GroupedObservable<TKey, TValue>) => Observable<any> = (_) => timer(300000)
): OperatorFunction<TValue, TResult> {
  return (o$) =>
    o$.pipe(
      groupBy(keySelector, (v) => v, durationSelector),
      mergeMap(mapFn)
    );
}

/**
 * Debounces the observable stream after the first occurrence.
 *
 * Useful in circumstances where we want to debounce an api query (e.g. when parameters change on keystroke)
 * but don't want to delay the first query
 *
 * @param dueTimeInMs length of time in milliseconds in which observables are debounced
 */
export function debounceAllButFirst<TValue>(dueTimeInMs: number): MonoTypeOperatorFunction<TValue> {
  return (o$) => concat(o$.pipe(take(1)), o$.pipe(debounceTime(dueTimeInMs)));
}
